/**
 * Video Element Schema
 */

import type { ElementSchema, MediaFieldSchema, TextFieldSchema, BooleanFieldSchema, SelectFieldSchema } from '@ai-content-hub/content-kit';

export const defaultVideoElementI18n = {
    en: {
        uploadVideo: 'Upload video',
        play: 'Play',
        pause: 'Pause',
        videoSource: 'Video source',
        videoTitlePlaceholder: 'Video title...',
        posterImage: 'Poster image',
        posterImageHint: 'Thumbnail shown before video plays',
        videoPosterMediaTitle: 'Video poster',
        aspectRatio: 'Aspect ratio',
        aspect169: '16:9 (Widescreen)',
        aspect43: '4:3 (Standard)',
        aspect11: '1:1 (Square)',
        aspect916: '9:16 (Portrait)',
        playbackOptions: 'Playback options',
        autoplay: 'Autoplay',
        loop: 'Loop',
        muted: 'Muted',
        showControls: 'Controls',
    },
    vi: {
        uploadVideo: 'Tải video lên',
        play: 'Phát',
        pause: 'Tạm dừng',
        videoSource: 'Nguồn video',
        videoTitlePlaceholder: 'Tiêu đề video...',
        posterImage: 'Ảnh poster',
        posterImageHint: 'Hình thu nhỏ hiển thị trước khi phát video',
        videoPosterMediaTitle: 'Poster video',
        aspectRatio: 'Tỷ lệ khung hình',
        aspect169: '16:9 (Màn rộng)',
        aspect43: '4:3 (Tiêu chuẩn)',
        aspect11: '1:1 (Vuông)',
        aspect916: '9:16 (Dọc)',
        playbackOptions: 'Tùy chọn phát',
        autoplay: 'Tự động phát',
        loop: 'Lặp lại',
        muted: 'Tắt tiếng',
        showControls: 'Điều khiển',
    },
};

export interface VideoElementData {
    src: string;
    title: string;
    poster?: string;
    autoplay: boolean;
    loop: boolean;
    muted: boolean;
    showControls: boolean;
    aspectRatio: '16:9' | '4:3' | '1:1' | '9:16';
}

export const videoElementSchema: ElementSchema<VideoElementData> = {
    type: 'element-video',
    title: 'Video',
    description: 'Video player with playback controls',
    version: '1.0.0',
    category: 'media',
    icon: 'Video',
    tags: ['video', 'media', 'movie', 'clip'],
    fields: [
        {
            name: 'src',
            type: 'media',
            label: 'Video File',
            description: 'Upload or enter video URL',
            required: true,
            mediaType: 'video',
            accept: ['video/*'],
        } as MediaFieldSchema,
        {
            name: 'title',
            type: 'text',
            label: 'Title',
            description: 'Video title for accessibility',
            placeholder: 'Video title...',
        } as TextFieldSchema,
        {
            name: 'poster',
            type: 'media',
            label: 'Poster Image',
            description: 'Thumbnail image before video plays',
            mediaType: 'image',
        } as MediaFieldSchema,
        {
            name: 'aspectRatio',
            type: 'select',
            label: 'Aspect Ratio',
            defaultValue: '16:9',
            options: [
                { value: '16:9', label: '16:9 (Widescreen)' },
                { value: '4:3', label: '4:3 (Standard)' },
                { value: '1:1', label: '1:1 (Square)' },
                { value: '9:16', label: '9:16 (Portrait)' },
            ],
        } as SelectFieldSchema,
        {
            name: 'autoplay',
            type: 'boolean',
            label: 'Autoplay',
            description: 'Start playing automatically (muted required)',
            defaultValue: false,
        } as BooleanFieldSchema,
        {
            name: 'loop',
            type: 'boolean',
            label: 'Loop',
            description: 'Repeat when finished',
            defaultValue: false,
        } as BooleanFieldSchema,
        {
            name: 'muted',
            type: 'boolean',
            label: 'Muted',
            description: 'Start with sound muted',
            defaultValue: false,
        } as BooleanFieldSchema,
        {
            name: 'showControls',
            type: 'boolean',
            label: 'Show Controls',
            description: 'Display playback controls',
            defaultValue: true,
        } as BooleanFieldSchema,
    ],
    defaultData: {
        src: '',
        title: '',
        poster: '',
        autoplay: false,
        loop: false,
        muted: false,
        showControls: true,
        aspectRatio: '16:9',
    },
    i18n: defaultVideoElementI18n,
};

